import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import OwnerSubHeader from "@/components/OwnerSubHeader";
import SiteLogoField from "@/components/SiteLogoField";
import { Loader2, Save, AlertTriangle, Check, Settings2, Skull, Trash2, ShieldAlert, Copy } from "lucide-react";
import { can } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";

/**
 * SiteConfig — mirrors the mobile app's Site Configuration screen.
 * Backend: GET /api/admin/site-config, PATCH /api/admin/site-config
 */
export default function SiteConfig() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/site-config"); setCfg(data || {}); }
      catch (ex) { setErr(apiError(ex)); setCfg({}); }
    })();
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setArr = (k, v) => set(k, v.split(",").map((s) => s.trim()).filter(Boolean));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.patch("/admin/site-config", cfg);
      setMsg({ type: "ok", text: "Site configuration saved." });
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally { setBusy(false); }
  };

  if (cfg === null) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center gap-3 text-muted">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading site config…
      </div>
    );
  }

  const shifts = (cfg.shifts || []).join(", ");
  const areas = (cfg.work_areas || cfg.areas || []).join(", ");
  const hazards = (cfg.hazard_types || cfg.hazards || []).join(", ");

  return (
    <div className="space-y-5" data-testid="site-config-page">
      <OwnerSubHeader
        title="Site Configuration"
        subtitle="Shifts · Work areas · Hazard catalogue · Modules"
        right={
          <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="site-config-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        }
      />

      {err && <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="panel p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-1 flex items-center gap-2">
            <Settings2 className="w-3.5 h-3.5" /> Company / Site
          </div>
          <div className="field">
            <label>Company name</label>
            <input className="input" value={cfg.company || ""} onChange={(e) => set("company", e.target.value)} placeholder="Rio Tinto" data-testid="cfg-company" />
          </div>
          <div className="field">
            <label>Default site</label>
            <input className="input" value={cfg.site_code || ""} onChange={(e) => set("site_code", e.target.value)} data-testid="cfg-site-code" />
          </div>
          <div className="field">
            <label>Timezone</label>
            <input className="input" value={cfg.timezone || ""} onChange={(e) => set("timezone", e.target.value)} placeholder="Australia/Perth" data-testid="cfg-timezone" />
          </div>
        </div>

        <div className="panel p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Operations</div>
          <div className="field">
            <label>Shifts (comma-separated)</label>
            <input className="input" value={shifts} onChange={(e) => setArr("shifts", e.target.value)} placeholder="morning, afternoon, night" data-testid="cfg-shifts" />
          </div>
          <div className="field">
            <label>Work areas</label>
            <textarea className="input" rows={3} value={areas} onChange={(e) => setArr("work_areas", e.target.value)} placeholder="Northern Pit, Southern Pit, Workshop" data-testid="cfg-areas" />
          </div>
          <div className="field">
            <label>Hazard catalogue</label>
            <textarea className="input" rows={3} value={hazards} onChange={(e) => setArr("hazard_types", e.target.value)} placeholder="Stored Energy, Hot Work, Working at Height, Confined Space, Chemicals" data-testid="cfg-hazards" />
          </div>
        </div>
      </div>

      {/* White-label PDF header logo (Feb 28) — per-site custom branding.
          Prod schema uses `custom_logo_data_url`, older builds `pdf_header_logo`.
          We write BOTH keys so it round-trips regardless of which one the
          shared backend persists, and read whichever it hands back on GET. */}
      <SiteLogoField
        value={cfg.custom_logo_data_url || cfg.pdf_header_logo || ""}
        onChange={(v) => { set("custom_logo_data_url", v); set("pdf_header_logo", v); }}
        siteCode={cfg.site_code}
      />

      {msg && (
        <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`} data-testid="cfg-msg">
          {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
        </div>
      )}

      {/* ======================================================================
          Danger Zone — Clean-slate reset for this site.
          Josh (Feb 9): "delete all people and inspections and breakdowns and
          start new… just leave mc/whc001". Owner-tier only. Requires typing
          the site code to confirm. Tries the backend admin reset endpoint
          first; if it isn't wired yet on the API side, we surface the exact
          mongo command the DBA needs to run so nobody is left guessing.
          ================================================================== */}
      {can(user, "manageSites") && (
        <div
          className="panel p-5 mt-6"
          style={{ borderColor: "#a51919", background: "linear-gradient(180deg, rgba(165,25,25,0.06) 0%, rgba(10,10,10,0.4) 100%)" }}
          data-testid="site-danger-zone"
        >
          <div className="flex items-start gap-3 flex-wrap">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "rgba(165,25,25,0.16)", border: "1px solid #a51919", color: "#ff9b9b" }}
            >
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-display font-black text-xl tracking-tight" style={{ color: "#ff9b9b" }}>
                Danger Zone · Reset Site Data
              </div>
              <div className="text-sm text-white/70 mt-1 max-w-xl">
                Permanently deletes every inspection, breakdown, handover, PPF, hazard,
                shift summary and swing at this site, plus every user account
                except the current owner. <b className="text-white">Assets and site
                configuration are kept.</b> This action is irreversible.
              </div>
            </div>
            <button
              className="btn btn-sm shrink-0"
              style={{ background: "#a51919", color: "#fff", border: "1px solid #ff5a5a" }}
              onClick={() => setResetOpen(true)}
              data-testid="reset-site-open"
            >
              <Trash2 className="w-4 h-4" /> Reset Site Data
            </button>
          </div>
        </div>
      )}

      {resetOpen && (
        <ResetSiteModal
          siteCode={cfg.site_code || cfg.default_site || localStorage.getItem("sl_active_site") || "WHC-MC001"}
          onClose={() => setResetOpen(false)}
        />
      )}
    </div>
  );
}

// ============================================================================
// ResetSiteModal — confirm-by-typing modal.
// Tries POST /api/admin/sites/:site_id/reset with the payload the backend
// runbook expects. If the backend hasn't shipped the endpoint yet, we detect
// the 404/405/501 and switch into a "hand this to your DBA" panel that shows
// the exact mongo command + a Copy button so nothing is lost in translation.
// ============================================================================
function ResetSiteModal({ siteCode, onClose }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("confirm"); // confirm | success | fallback
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);

  const target = String(siteCode || "").toUpperCase();
  const armed = typed.trim().toUpperCase() === target;

  const mongoCmd = `mongosh "$MONGO_URL" - <<'EOF'
db.inspections.deleteMany({});
db.breakdowns.deleteMany({});
db.handovers.deleteMany({});
db.ppfs.deleteMany({});
db.hazards.deleteMany({});
db.shift_summaries.deleteMany({});
db.swings.deleteMany({});
db.users.deleteMany({
  username: { $nin: ["control","CONTROL"] },
  role: { $nin: ["owner","control"] }
});
print("Wipe complete for ${target}. Assets + sites preserved.");
EOF`;

  const runReset = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/admin/sites/${encodeURIComponent(target)}/reset`, {
        keep_users: ["control"],
        keep_collections: ["sites", "assets"],
        wipe_collections: [
          "inspections", "breakdowns", "handovers", "ppfs",
          "hazards", "shift_summaries", "swings",
        ],
      });
      setReport(data || {});
      setPhase("success");
    } catch (ex) {
      const status = ex?.response?.status;
      // Endpoint not implemented on the backend yet → hand off to DBA.
      if (status === 404 || status === 405 || status === 501) {
        setPhase("fallback");
      } else {
        setError(apiError(ex));
      }
    } finally { setBusy(false); }
  };

  const copyCmd = async () => {
    try { await navigator.clipboard.writeText(mongoCmd); } catch { /* clipboard blocked */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="reset-site-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }} onClick={busy ? undefined : onClose} />
      <div
        className="relative w-full max-w-lg panel p-6"
        style={{ borderColor: "#a51919", background: "var(--surface)" }}
      >
        <div className="flex items-center gap-3 mb-4">
          <Skull className="w-6 h-6" style={{ color: "#ff5a5a" }} />
          <div>
            <div className="font-display font-black text-2xl tracking-tight" style={{ color: "#ff9b9b" }}>
              Reset {target}?
            </div>
            <div className="text-xs text-muted uppercase tracking-widest font-bold">
              This action cannot be undone
            </div>
          </div>
        </div>

        {phase === "confirm" && (
          <>
            <div className="text-sm text-white/85 space-y-2">
              <p>
                You are about to permanently delete <b className="text-white">every</b> inspection,
                breakdown, handover, PPF, hazard, shift summary and swing at <b className="text-white">{target}</b>.
                Every user except CONTROL will be removed.
              </p>
              <p className="text-white/60">
                Assets and the site record itself will stay intact.
              </p>
            </div>
            <div className="field mt-4">
              <label className="!text-[10px]">Type <b className="text-white">{target}</b> to confirm</label>
              <input
                className="input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                placeholder={target}
                data-testid="reset-site-confirm-input"
              />
            </div>
            {error && (
              <div className="pill pill-red !text-xs !py-2 !px-3 justify-start mt-3" data-testid="reset-site-error">
                <AlertTriangle className="w-3.5 h-3.5" /> {error}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 mt-5">
              <button className="btn btn-ghost" onClick={onClose} disabled={busy} data-testid="reset-site-cancel">Cancel</button>
              <button
                className="btn"
                style={{
                  background: armed ? "#a51919" : "rgba(165,25,25,0.35)",
                  color: "#fff",
                  border: `1px solid ${armed ? "#ff5a5a" : "#5a1717"}`,
                  cursor: armed ? "pointer" : "not-allowed",
                }}
                disabled={!armed || busy}
                onClick={runReset}
                data-testid="reset-site-execute"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Wipe now
              </button>
            </div>
          </>
        )}

        {phase === "success" && (
          <div className="space-y-4">
            <div className="pill pill-green !text-sm !py-2 !px-3 justify-start" data-testid="reset-site-success">
              <Check className="w-4 h-4" /> {target} reset complete.
            </div>
            {report && (
              <pre
                className="text-[11px] font-mono-tight p-3 rounded-md whitespace-pre-wrap"
                style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--border)", color: "#4ade80" }}
              >
                {JSON.stringify(report, null, 2)}
              </pre>
            )}
            <div className="flex justify-end">
              <button className="btn btn-primary" onClick={onClose} data-testid="reset-site-done">Done</button>
            </div>
          </div>
        )}

        {phase === "fallback" && (
          <div className="space-y-3" data-testid="reset-site-fallback">
            <div className="pill pill-yellow !text-sm !py-2 !px-3 justify-start">
              <AlertTriangle className="w-4 h-4" /> Backend reset endpoint not yet available
            </div>
            <div className="text-sm text-white/80">
              Your API doesn&apos;t expose <code className="text-white bg-black/40 px-1">POST /api/admin/sites/{target}/reset</code> yet.
              Hand the command below to your DBA / backend agent — it does exactly the same wipe against mongo directly.
            </div>
            <div className="relative">
              <pre
                className="text-[11px] font-mono-tight p-3 rounded-md whitespace-pre-wrap max-h-64 overflow-auto"
                style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--border)", color: "#ffb347" }}
                data-testid="reset-site-mongo-cmd"
              >
                {mongoCmd}
              </pre>
              <button
                className="btn btn-sm absolute top-2 right-2"
                onClick={copyCmd}
                title="Copy to clipboard"
                data-testid="reset-site-copy"
              >
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
            <div className="flex justify-end">
              <button className="btn btn-primary" onClick={onClose} data-testid="reset-site-close-fallback">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
